// Три состояния экрана: загрузка / ошибка («не ответили») / ответ. Ошибку никогда не рисуем как «пусто».
import { type ReactNode, type RefObject, useLayoutEffect, useRef } from 'react';
import { useInRouterContext, useLocation, useNavigate } from 'react-router-dom';
import { ApiError } from '../api/client';
import type { EmptyState } from '../api/types';
import { t } from '../i18n/i18n';
import emptyArt from '../assets/states/empty.png';
import errorArt from '../assets/states/error.png';
import offlineArt from '../assets/states/offline.png';
// Figma «Поиск / Ничего не найдено» (772:33118): калитка на газоне, выгрузка узла макета 2x
import searchArt from './art/search-empty.png';
// Figma «Поиск избранного / Find Your …» (1073:21770): лупа, вырезана из экрана макета 2x с прозрачным фоном
import findArt from './art/search-find.png';
import { useOfflinePlaceholder } from './OfflineBanner';
import styles from './States.module.css';

export type StateArt = 'empty' | 'error' | 'offline' | 'search' | 'find';
const ART: Record<StateArt, string> = { empty: emptyArt, error: errorArt, offline: offlineArt, search: searchArt, find: findArt };
/** Иллюстрация сервера (empty_state.illustration) → наша; незнакомая — календарь «пусто». */
const ILLUSTRATION: Record<string, StateArt> = { search_empty: 'search' };
export const artOf = (illustration: string | undefined): StateArt => (illustration && ILLUSTRATION[illustration]) || 'empty';

/** Картинка «нет сети» — в кэш браузера при старте оболочки: без сети её уже не скачать (HQ-1322). */
export function preloadOfflineArt() {
  if (typeof Image !== 'undefined') new Image().src = offlineArt;
}

/** Заголовок, которому до одной строки не хватает не больше 15 % ширины, ужимается ровно под строку — иначе обычный перенос по словам. */
const ONE_LINE_FLOOR = 0.85;

/** Заголовок состояния — одной строкой, как в Figma Placeholder (342/369 × 264: 128 · 16 · заголовок 34 · 8 · подпись 22 · 16 ·
 *  кнопка 40, HQ-2213). «Oops, something went wrong» 28/500 в SF Pro макета — 342, а в Roboto ≈ 354 и в Inter ≈ 382: на 393
 *  и 360 строка переносилась, и блок вырастал до 293–298. Ширина строки зависит от шрифта устройства, поэтому её меряем:
 *  не влезла на ≤ 15 % — кегль ужимается под строку при прежней высоте строки (влезла — кегль CSS как есть, при 1× на 393 — 28). Пересчёт — на смену
 *  ширины блока, шрифта (×1.3, ×2) и догрузку начертаний. */
function useOneLineTitle(ref: RefObject<HTMLDivElement | null>, title: string | undefined) {
  useLayoutEffect(() => {
    const el = ref.current;
    const box = el?.parentElement;
    if (!el || !box) return;
    const fit = () => {
      el.style.fontSize = '';
      el.style.lineHeight = '';
      el.style.whiteSpace = 'nowrap';
      el.style.width = 'max-content';
      el.style.maxWidth = 'none';
      const natural = el.getBoundingClientRect().width;
      el.style.whiteSpace = '';
      el.style.width = '';
      el.style.maxWidth = '';
      const cs = getComputedStyle(box);
      const ratio = (box.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight)) / natural;
      if (ratio < 1 && ratio >= ONE_LINE_FLOOR) {
        const base = getComputedStyle(el);
        el.style.lineHeight = base.lineHeight; // строка заголовка — прежней высоты (34 при 28), ужимаются только буквы
        el.style.fontSize = `${Math.floor(parseFloat(base.fontSize) * ratio * 100) / 100}px`;
      }
    };
    fit();
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(fit);
    ro?.observe(box);
    const fonts = typeof document === 'undefined' ? undefined : document.fonts;
    fonts?.addEventListener?.('loadingdone', fit);
    return () => { ro?.disconnect(); fonts?.removeEventListener?.('loadingdone', fit); };
  }, [ref, title]);
}

/** Иллюстрация, заголовок и подпись по центру экрана — так в Figma нарисованы пустые экраны и ошибки.
 *  Без заголовка — только подпись (Figma «Nothing found» поиска). */
export function StateView({ art, title, text, children, longWord, compact }: {
  art: StateArt; title?: string; text?: string | null; children?: ReactNode;
  /** Состояние внутри раздела (вкладка, блок под шапкой и чипами): высота по содержимому, без 60vh — иначе кнопка
   *  повтора уходит под плашку «нет сети» (HQ-1659 v2). */
  compact?: boolean;
  /** Заголовок из одного длинного слова (ta «கிடைக்கவில்லை» при ×2 шире 360): перенос внутри слова, а не вылет за экран. */
  longWord?: boolean;
}) {
  const titleRef = useRef<HTMLDivElement>(null);
  useOneLineTitle(titleRef, title);
  return (
    <div className={compact ? `${styles.center} ${styles.compact}` : styles.center}>
      <img className={styles.art} src={ART[art]} alt="" width={128} height={128} />
      {title && <div ref={titleRef} className={longWord ? `${styles.title} ${styles.titleLong}` : styles.title}>{title}</div>}
      {text && <div className={styles.text}>{text}</div>}
      {children}
    </div>
  );
}

/** Запрос, который ещё грузится: если первая попытка уже не удалась (сеть, 5xx, 429 — клиент повторяет с паузой
 *  сервера), показываем ошибку с «Повторить», а не вечный скелетон. Запрос на паузе без сети (fetchStatus 'paused',
 *  failureCount 0) — сразу «нет сети» с повтором (HQ-1658). */
interface PendingQuery { failureCount: number; failureReason: unknown; fetchStatus?: string; refetch: () => unknown }

/** Скелетон: силуэт карточек со строками вместо спиннера — экран не «прыгает», когда приходит ответ. */
export function Loading({ cards = 3, rows = 3, query, compact }: {
  cards?: number; rows?: number; query?: PendingQuery;
  /** Внутри раздела: ошибка/«нет сети» вместо скелетона — по высоте содержимого (см. StateView). */
  compact?: boolean;
}) {
  if (query && query.failureCount > 0) return <ErrorView error={query.failureReason} onRetry={() => void query.refetch()} compact={compact} />;
  // сеть пропала до первого ответа: запрос на паузе (onlineManager), попыток нет — без этого скелетон висел бы вечно
  if (query?.fetchStatus === 'paused') return <ErrorView error={new ApiError('network', 0, null)} onRetry={() => void query.refetch()} compact={compact} />;
  return (
    <div className={styles.skeleton} role="status" aria-live="polite" aria-label={t('tg_loading')}>
      {Array.from({ length: cards }, (_, c) => (
        <div key={c} className={styles.skCard} aria-hidden="true">
          <span className={styles.skTitle} />
          {Array.from({ length: rows }, (_, r) => (
            <div key={r} className={styles.skRow}>
              <span className={styles.skDot} />
              <span className={styles.skLine} style={{ width: `${70 - ((r + c) % 3) * 15}%` }} />
              <span className={styles.skTail} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/** Запрос экрана, у которого ещё нет данных (ни ответа, ни кэша). */
interface ScreenQuery extends PendingQuery { isError: boolean; error: unknown }

/** Единый паттерн «нет данных» (HQ-1060, Figma 772:33133 / 772:33139): шапка, верхние вкладки и нижняя панель остаются
 *  у экрана, этот блок занимает только область контента. Ошибка — «нет сети»/«ошибка сервера» с «Повторить»; запрос на
 *  паузе без сети — сразу «нет сети», а не вечный скелетон (вернётся сеть — запрос пойдёт сам); иначе скелетон. */
export function QueryFallback({ query, cards, rows, skeleton, compact }: {
  query: ScreenQuery; cards?: number; rows?: number; compact?: boolean;
  /** Свой скелетон экрана (матч — пустые карточки макета 844:15773). */
  skeleton?: ReactNode;
}) {
  const retry = () => void query.refetch();
  if (query.isError) return <ErrorView error={query.error} onRetry={retry} compact={compact} />;
  // сеть пропала до первого ответа: запрос на паузе (onlineManager), ответа не будет, пока сеть не вернётся
  if (query.fetchStatus === 'paused') return <ErrorView error={new ApiError('network', 0, null)} onRetry={retry} compact={compact} />;
  if (skeleton && query.failureCount === 0) return <>{skeleton}</>;
  return <Loading cards={cards} rows={rows} query={query} compact={compact} />;
}

/** Полоса вкладок, пока сервер не прислал tab_bar: серые плашки той же высоты — содержимое не прыгает. */
export function TabsSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className={styles.skTabs} aria-hidden="true">
      {Array.from({ length: count }, (_, i) => <span key={i} className={styles.skTab} />)}
    </div>
  );
}

/** Кусок экрана не скачался (lazy-импорт без сети, HQ-1112): Chrome — «Failed to fetch dynamically imported module»,
 *  Safari — «Importing a module script failed», Firefox — «error loading dynamically imported module», Vite —
 *  «Unable to preload CSS». Это та же «нет сети», а не «ошибка сервера». */
export function isChunkLoadError(error: unknown): boolean {
  return error instanceof Error
    && /dynamically imported module|Importing a module script failed|Unable to preload CSS|Loading (CSS )?chunk/i.test(error.message);
}

/** Ошибка экрана для ErrorView: несгруженный кусок экрана — как обрыв сети. */
export function screenError(error: unknown): unknown {
  return isChunkLoadError(error) ? new ApiError('network', 0, null) : error;
}

/** Figma Placeholder «Server Error» / «Нет интернета»: тексты и кнопка макета (Reload / Try Again). */
export function ErrorView({ error, onRetry, compact }: { error: unknown; onRetry: () => void; compact?: boolean }) {
  const inRouter = useInRouterContext();
  const offline = error instanceof ApiError && error.kind === 'network';
  // «нет сети» на всю область — плашка «нет сети» молчит (Figma 1073:23495); compact — раздел под шапкой с данными, плашка
  // остаётся (HQ-2225)
  useOfflinePlaceholder(offline && !compact);
  // 404: матча/турнира больше нет (старая ссылка из пуша) — повтор снова даст 404 (4xx клиент не повторяет), ведём назад
  if (error instanceof ApiError && error.status === 404) {
    return <StateView art="search" title={t('tg_not_found')} longWord compact={compact}>{inRouter && <NotFoundBack />}</StateView>;
  }
  // 429: сервер просит подождать — свой текст, кнопка та же
  const busy = error instanceof ApiError && error.status === 429;
  return (
    <StateView art={offline ? 'offline' : 'error'} title={offline ? t('mc_offline') : t('tg_error_title')}
      text={offline ? t('mc_offline_check') : busy ? t('tg_error_busy') : t('tg_error_text')} compact={compact}>
      <button className={styles.button} onClick={onRetry}>{offline ? t('tg_try_again') : t('tg_reload')}</button>
    </StateView>
  );
}

/** «Назад» экрана «не найдено»: шаг по истории; открыли глубокой ссылкой (истории нет) — вкладка по умолчанию. */
function NotFoundBack() {
  const navigate = useNavigate();
  const location = useLocation();
  const back = () => (location.key !== 'default' ? navigate(-1) : navigate('/', { replace: true }));
  return <button className={styles.button} onClick={back}>{t('tg_back')}</button>;
}

/** Серверный empty_state: рисуется ВМЕСТО экрана. Кнопка — только для поддержанных действий. */
export function EmptyStateView({ state, onReload, compact }: { state: EmptyState; onReload: () => void; compact?: boolean }) {
  const action = state.action?.kind === 'reload' ? state.action : null;
  return (
    <StateView art={artOf(state.illustration)} title={state.title} text={state.text} compact={compact}>
      {action && <button className={styles.button} onClick={onReload}>{action.label}</button>}
    </StateView>
  );
}
